import React from 'react';
import './_InspectionReviewFeature.scss';

/**
 * Review controls on the Modify Inspection → Features tab (SW2-2248).
 *
 * Its own component rather than more JSX inside InspectionModal.tsx, which is already ~850 lines
 * against a 400-line feature cap.
 *
 * The caller is responsible for rendering this only when the tenant holds the
 * `Inspection Review` feature — same as the Asset Tracking block beside it.
 */

export type ReviewerRoleOption = { role_id: number; role_name: string };
export type ReviewerUserOption = {
  user_id: number;
  user_name: string;
  email: string;
};

type Props = {
  reviewRequired: boolean;
  setReviewRequired: (value: boolean) => void;
  reviewByManager: boolean;
  setReviewByManager: (value: boolean) => void;
  reviewerRoleId: number | null;
  setReviewerRoleId: (value: number | null) => void;
  reviewerUserId: number | null;
  setReviewerUserId: (value: number | null) => void;
  showReviewSignature: boolean;
  setShowReviewSignature: (value: boolean) => void;
  reviewerRoles: ReviewerRoleOption[];
  reviewerUsers: ReviewerUserOption[];
  /** True when a control is rendered above this block, so it needs separating space. */
  hasBlockAbove?: boolean;
};

const InspectionReviewFeature = ({
  reviewRequired,
  setReviewRequired,
  reviewByManager,
  setReviewByManager,
  reviewerRoleId,
  setReviewerRoleId,
  reviewerUserId,
  setReviewerUserId,
  showReviewSignature,
  setShowReviewSignature,
  reviewerRoles,
  reviewerUsers,
  hasBlockAbove = false,
}: Props) => {
  // The role/user pair is the fallback when manager mode is on, and the reviewer itself when it
  // is off — so the label has to change with the mode or it misdescribes what it does.
  const reviewerFieldsLegend = reviewByManager
    ? 'If no manager is found, review by'
    : 'Review by';

  const noReviewerSet = !reviewerRoleId && !reviewerUserId;

  return (
    <div
      className={
        hasBlockAbove ? 'inspection-review-feature inspection-review-feature--spaced' : 'inspection-review-feature'
      }
    >
      <label className='inspection-review-feature__checkbox-label'>
        <input
          type='checkbox'
          checked={reviewRequired}
          onChange={(e) => setReviewRequired(e.target.checked)}
          className='inspection-review-feature__checkbox-input'
        />
        <span>Require Review Before Distribution</span>
      </label>
      <p className='inspection-review-feature__help-text'>
        When enabled, inspections using this form must be approved by a reviewer
        before the inspection complete notification is sent.
      </p>

      {reviewRequired && (
        <>
          <label className='inspection-review-feature__checkbox-label inspection-review-feature__checkbox-label--manager'>
            <input
              type='checkbox'
              checked={reviewByManager}
              onChange={(e) => setReviewByManager(e.target.checked)}
              className='inspection-review-feature__checkbox-input'
            />
            <span>Review by the inspector&apos;s manager</span>
          </label>
          <p className='inspection-review-feature__help-text'>
            Routes each review to the manager of whoever signed. Inspectors with
            no manager on record fall back to the reviewer set below, which is
            why one is always required.
          </p>

          <div className='inspection-review-feature__field'>
            <label className='inspection-review-feature__field-label'>{reviewerFieldsLegend}</label>
            <select
              value={reviewerRoleId ?? ''}
              onChange={(e) =>
                setReviewerRoleId(e.target.value ? Number(e.target.value) : null)
              }
              className='inspection-review-feature__select'
            >
              <option value=''>Select a role…</option>
              {reviewerRoles.map((role) => (
                <option key={role.role_id} value={role.role_id}>
                  {role.role_name}
                </option>
              ))}
            </select>
            <p className='inspection-review-feature__help-text inspection-review-feature__help-text--flush'>
              Anyone on this project holding the role can approve. Only roles
              that are allowed to review are listed.
            </p>
          </div>

          <div className='inspection-review-feature__field'>
            <label className='inspection-review-feature__field-label'>Or a specific person</label>
            <select
              value={reviewerUserId ?? ''}
              onChange={(e) =>
                setReviewerUserId(e.target.value ? Number(e.target.value) : null)
              }
              className='inspection-review-feature__select'
            >
              <option value=''>No specific person</option>
              {reviewerUsers.map((user) => (
                <option key={user.user_id} value={user.user_id}>
                  {user.user_name || user.email}
                </option>
              ))}
            </select>
          </div>

          {noReviewerSet && (
            <p className='inspection-review-feature__help-text inspection-review-feature__help-text--warning'>
              Choose a reviewer role or a specific person. Without one, an
              inspection could be signed and then never released for
              distribution.
            </p>
          )}

          <label className='inspection-review-feature__checkbox-label inspection-review-feature__checkbox-label--signature'>
            <input
              type='checkbox'
              checked={showReviewSignature}
              onChange={(e) => setShowReviewSignature(e.target.checked)}
              className='inspection-review-feature__checkbox-input'
            />
            <span>Show Approval Signature</span>
          </label>
          <p className='inspection-review-feature__help-text'>
            When enabled, the reviewer&apos;s approval signature appears on the
            certification page and the printed report. The reviewer always sees
            it, because they have to sign it.
          </p>
        </>
      )}
    </div>
  );
};

export default InspectionReviewFeature;
